import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from "react-router-dom";

import { useSelector } from "react-redux";
import PageWrapper from '@/utils/PageWrapper';
import { getUrls } from '@/utils/urls/getUrls';

// pages
import Letters from '@/views/pages/Letters/Letters';
import Letter from '@/views/pages/Letter/Letter';
import Templates from '@/views/pages/Templates/Templates';
import Dictionary from '@/views/pages/Dictionary/Dictionary';
import Login from '@/views/pages/Login/Login';
import ResetPassword from '@/views/pages/ResetPassword/ResetPassword';
import AddLetter from '@/views/pages/AddLetter/AddLetter';
import ChangePassword from '@/views/pages/ChangePassword/ChangePassword';
import CookiesInfo from '@/views/pages/CookiesInfo/CookiesInfo';
import Departments from '@/views/pages/Departments/Departments';
import Users from '@/views/pages/Users/Users';
import SimpleText from '@/views/pages/SimpleText/SimpleText';
import PasswordRemind from '@/views/pages/PasswordRemind/PasswordRemind';
import SimpleTextAdvices from '@/views/pages/SimpleTextAdvices/SimpleTextAdvices';
import SimpleTextAdvice from '@/views/pages/SimpleTextAdvice/SimpleTextAdvice';
import SimpleTextMyAdvices from '@/views/pages/SimpleTextMyAdvices/SimpleTextMyAdvices';
import SimpleTextAdvicesAdmin from '@/views/pages/SimpleTextAdvicesAdmin/SimpleTextAdvicesAdmin';
import TemplatesManagement from '@/views/pages/TemplatesManagement/TemplatesManagement';
import GenerateReport from '@/views/pages/GenerateReport/GenerateReport';

import Template from '@/views/pages/Template/Template';

import Page404 from '@/views/pages/Errors/Page404';
import Page500 from '@/views/pages/Errors/Page500';

import MimSuccess from '@/views/pages/Mim/MimSuccess';
import MimFailure from '@/views/pages/Mim/MimFailure';

import { templateComponentModes } from '@/views/pages/Template/Template';

import { cookieService } from "@/services/cookie";

const Router = () => {
	const { simpleText, letters } = getUrls();
	const storageCsrf = cookieService.getToken();

	const isLoggedIn = useSelector(state => state.auth.isLoggedIn);

	return (
		<Suspense fallback={ null }>
			{/* urlAccessMode - 0: reader/editor/admin, 1: editor/admin , 2: admin only*/}
			<Routes>
				{/* AUTH */}
				<Route
					path='/login'
					element={ <Login /> }
				/>
				<Route
					path='/password-remind'
					element={ <PasswordRemind /> }
				/>
				<Route
					path='/password-set'
					element={ <PasswordRemind isSetPassword={ true } /> }
				/>
				<Route
					path='/reset-password'
					element={ <ResetPassword /> }
				/>
				<Route
					path='/change-password'
					element={
						<PageWrapper>
							<ChangePassword />
						</PageWrapper>
					}
				/>
				<Route
					path='/letters'
					element={
						<PageWrapper>
							<Letters />
						</PageWrapper>
					}
				/>
				<Route
					path='/templates'
					element={
						<PageWrapper>
							<Templates />
						</PageWrapper>
					}
				/>
				<Route
					path='/create-template/:id'
					element={
						<PageWrapper urlAccessMode={ 1 }>
							<Template mode={ templateComponentModes.createMode }/>
						</PageWrapper>
					}
				/>
				<Route
					path='/template/:id'
					element={
						<PageWrapper>
							<Template mode={ templateComponentModes.useMode }/>
						</PageWrapper>
					}
				/>
				<Route
					path='/edit-template/:id'
					element={
						<PageWrapper urlAccessMode={ 1 }>
							<Template mode={ templateComponentModes.editMode }/>
						</PageWrapper>
					}
				/>
				<Route
					path='/dict'
					element={
						<PageWrapper>
							<Dictionary />
						</PageWrapper>
					}
				/>
				<Route
					path='/departments'
					element={
						<PageWrapper urlAccessMode={ 1 }>
							<Departments />
						</PageWrapper>
					}
				/>
				<Route
					path='/users'
					element={
						<PageWrapper urlAccessMode={ 2 }>
							<Users />
						</PageWrapper>
					}
				/>
				<Route
					path='/add-letter'
					element={
						<PageWrapper urlAccessMode={ 1 }>
							<AddLetter />
						</PageWrapper>
					}
				/>
				<Route
					path='/simple-text-advices'
					exact
					element={
						<PageWrapper>
							<SimpleTextAdvices />
						</PageWrapper>
					}
				/>
				<Route
					path='/simple-text-advice/:id'
					exact
					element={
						<PageWrapper >
							<SimpleTextAdvice />
						</PageWrapper>
					}
				/>
				<Route
					path='/simple-text-my-advices'
					exact
					element={
						<PageWrapper urlAccessMode={ 1 }>
							<SimpleTextMyAdvices />
						</PageWrapper>
					}
				/>
				<Route
					path='/simple-text-advices-admin'
					exact
					element={
						<PageWrapper urlAccessMode={ 2 }>
							<SimpleTextAdvicesAdmin />
						</PageWrapper>
					}
				/>
				<Route
					path='/letter/:id'
					element={
						<PageWrapper>
							<Letter />
						</PageWrapper>
					}
				/>
				<Route
					path='/cookies'
					element={
						(storageCsrf || isLoggedIn) ? (
							<PageWrapper>
								<CookiesInfo />
							</PageWrapper>
						) : (
							<CookiesInfo />
						)
					}
				/>
				<Route
					path={ simpleText }
					element={
						<PageWrapper>
							<SimpleText />
						</PageWrapper>
					}
				/>
				<Route
					path={ 'generate-report' }
					element={
						<PageWrapper urlAccessMode={ 2 }>
							<GenerateReport />
						</PageWrapper>
					}
				/>
				<Route
					path={ 'templates-management' }
					element={
						<PageWrapper urlAccessMode={ 2 }>
							<TemplatesManagement />
						</PageWrapper>
					}
				/>
				<Route
					path='/page404'
					exact
					element={ <Page404 /> }
				/>
				<Route
					path='/page500'
					exact
					element={ <Page500 /> }
				/>
				<Route
					path='/mim-failure'
					exact
					element={ <MimFailure /> }
				/>
				<Route
					path='/mim-success'
					exact
					element={ <MimSuccess /> }
				/>
				<Route
					path="*"
					element={
						<Navigate
							to={ letters }
							replace
						/>
					}
				/>
			</Routes>
		</Suspense>
	);
};

export default React.memo(Router);
